import { INetwork } from '@/types'
import { isBtcChain, isMobileDevice, SUPPORTED_NETWORK, WALLET_BITGET } from '@/utils'
import { CustomError } from '@/utils/error'

import { BaseWallet } from './BaseWallet'
import {
  BitgetProvider,
  BitgetWalletInstance,
  IWalletConnectOptions,
  PsbtSignOptions,
  SignMessageOptions,
  TransactionOptions,
} from './types'

const TESTNET = 'testnet'
const MAINNET = 'livenet'

function getBitgetProvider(): BitgetWalletInstance | undefined {
  return window.bitkeep
}

export class BitgetWallet extends BaseWallet {
  name = 'Bitget Wallet'
  key = WALLET_BITGET
  private provider: BitgetProvider | null = null
  private connectedAddresses: Record<string, string> = {}

  private redirectToWallet(): void {
    window.location.href = `bitkeep://bkconnect?action=dapp&url=${encodeURIComponent(window.location.href)}`
  }

  private download(): void {
    throw new CustomError(
      'Bitget Wallet is not installed! <a target="_blank" class="underline" href="https://web3.bitget.com/">Download wallet</a>'
    )
  }

  private getWalletProvider(): BitgetWalletInstance | null {
    const provider = getBitgetProvider()
    if (provider) {
      return provider
    }
    this.download()
    return null
  }

  initialize() {
    if (!this.provider) {
      this.provider = this.getWalletProvider()
    }
  }

  isInstalled(): boolean {
    if (isMobileDevice()) {
      return true
    }
    return !!getBitgetProvider()
  }

  async connect(
    networkId: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    if (isMobileDevice() && !getBitgetProvider()) {
      this.redirectToWallet()
      throw new CustomError('Redirecting to Bitget Wallet...')
    }
    this.initialize()

    if (!this.provider) {
      throw new CustomError('Bitget wallet is not installed')
    }

    if (isBtcChain(networkId)) {
      const currentNetwork = await this.provider.unisat.getNetwork()

      if (currentNetwork !== TESTNET && networkId === SUPPORTED_NETWORK.BTC_TESTNET) {
        await this.provider.unisat.switchNetwork(TESTNET)
      }

      if (currentNetwork !== MAINNET && networkId === SUPPORTED_NETWORK.BTC) {
        await this.provider.unisat.switchNetwork(MAINNET)
      }

      const accounts = await this.provider.unisat.requestAccounts()
      const publicKey = await this.provider.unisat.getPublicKey()

      this.connectedAddresses[networkId] = accounts[0]

      return {
        address: accounts[0],
        networkInfo: options.network,
        compressedPublicKey: publicKey,
      }
    }

    throw new Error(`Network ${networkId} is not supported for automatic addition`)
  }

  async disconnect(): Promise<void> {
    if (!this.provider) {
      throw new CustomError('Bitget wallet is not installed')
    }

    this.provider = null
  }

  async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    networkId: string,
    options?: TransactionOptions
  ): Promise<string> {
    if (!this.provider) {
      throw new CustomError('Bitget wallet is not installed')
    }

    if (isBtcChain(networkId)) {
      return await this.provider.unisat.sendBitcoin(
        toAddress,
        amount.toString(),
        options?.feeRate ? { feeRate: options.feeRate } : undefined
      )
    }
    throw new Error(`Unsupported network ${networkId}`)
  }

  async getAddress(network?: string): Promise<string> {
    if (!this.provider) {
      throw new CustomError('Bitget wallet is not installed')
    }

    let accounts

    if (isBtcChain(network || '')) {
      accounts = await this.provider.unisat.requestAccounts()
      return accounts?.[0]
    }
    throw new Error('Unsupport network')
  }

  async signMessage(message: string, network: string, options?: SignMessageOptions): Promise<string> {
    if (!this.provider) {
      throw new CustomError('Bitget wallet is not installed')
    }

    if (isBtcChain(network || '')) {
      return await this.provider.unisat.signMessage(message, options?.btcSignMessageType || 'bip322-simple')
    }

    throw new Error('Upnsupport network')
  }

  async getCompressedPublicKey(network: string): Promise<string> {
    if (!this.provider) {
      throw new CustomError('Bitget wallet is not installed')
    }

    if (isBtcChain(network || '')) {
      return await this.provider.unisat.getPublicKey()
    }

    throw new Error('Unsupport network')
  }

  async signPsbt(psbt: string, network: string, options?: PsbtSignOptions): Promise<string> {
    if (!this.provider) {
      throw new CustomError('Bitget wallet is not installed')
    }
    if (isBtcChain(network || '')) {
      return await this.provider.unisat.signPsbt(psbt, options)
    }

    throw new Error(`SignPsbt is not supported for network ${network}`)
  }

  isDetected(): boolean {
    return !!getBitgetProvider()
  }

  priority(): number {
    const isBitgetWallet = () => {
      try {
        if (typeof window === 'undefined') return false

        const hasBitgetMethods = typeof window.bitkeep?.unisat?.getNetwork === 'function'

        const hasBitgetStructure = window.bitkeep?.unisat

        const hasBitgetUserAgent = typeof window !== 'undefined' && /BitKeep/i.test(navigator?.userAgent)

        return hasBitgetMethods && hasBitgetStructure && hasBitgetUserAgent
      } catch {
        return false
      }
    }

    if (isBitgetWallet() && isMobileDevice()) {
      return 0
    }

    return this.isInstalled() ? 0 : 1
  }
}
